import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";

import { getPartyShellChrome } from "../modePartyCards";
import { PartyMascot } from "../PartyMascot";
import type { PartyShellPalette } from "../../theme/theme";

export type PartyShareScoreRow = {
  label: string;
  value: string | number;
  color?: string;
  highlight?: boolean;
};

export type PartyShareStandingRow = {
  name: string;
  value: string;
  highlight?: boolean;
};

export type PartyShareResultCardModel = {
  shellId: string;
  dir: "rtl" | "ltr";
  lang: "ar" | "en";

  modeTitle: string;
  resultTitle: string;

  hero: string;
  sub?: string;

  scores?: PartyShareScoreRow[];
  standings?: PartyShareStandingRow[];
  meta?: string[];
};

const W = 1080;
const H = 1920;
const FOOTER_HANDLE = "@kanplays";

export const PartyShareResultCard = React.forwardRef<HTMLDivElement, PartyShareResultCardModel>(function PartyShareResultCard(
  model,
  ref,
) {
  const { t } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const chrome = getPartyShellChrome(model.shellId);
  const mint = (theme.palette as unknown as { secondary?: { main?: string } }).secondary?.main ?? "#00E5CC";

  return (
    <Box
      ref={ref}
      dir={model.dir}
      lang={model.lang}
      data-party-theme="midnight"
      data-party-type="rubik"
      sx={{
        width: W,
        height: H,
        position: "relative",
        overflow: "hidden",
        borderRadius: 6,
        background: chrome.bg as string,
        color: partyPal.ink,
        ["--hot" as string]: partyPal.hot,
        ["--gold" as string]: partyPal.gold,
        ["--violet" as string]: partyPal.violet,
        ["--mint" as string]: mint,
        ["--ink" as string]: partyPal.ink,
        ["--ink-dim" as string]: partyPal.inkDim,
        ["--ink-faint" as string]: partyPal.inkFaint,
        ["--paper" as string]: partyPal.paper,
        ["--paper-ink" as string]: partyPal.paperInk,
      }}
    >
      {/* soft vignette + tint */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1200px 900px at 50% 15%, rgba(255,255,255,0.10), transparent 55%), radial-gradient(900px 700px at 20% 90%, rgba(255,61,120,0.12), transparent 60%), linear-gradient(180deg, rgba(0,0,0,0.38), rgba(0,0,0,0.56))",
          pointerEvents: "none",
        }}
      />

      {/* lively sparkles */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.8,
          background:
            "radial-gradient(10px 10px at 12% 20%, rgba(255,193,94,0.55), transparent 70%), radial-gradient(14px 14px at 82% 18%, rgba(255,61,120,0.48), transparent 72%), radial-gradient(12px 12px at 18% 76%, rgba(138,108,241,0.46), transparent 72%), radial-gradient(9px 9px at 78% 72%, rgba(0,229,204,0.40), transparent 72%)",
        }}
      />

      {/* confetti blobs (static, export-friendly) */}
      <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.28 }}>
        {[
          { id: "guess", x: "10%", y: "14%", r: -14, s: 64 },
          { id: "act", x: "88%", y: "18%", r: 18, s: 56 },
          { id: "fast", x: "12%", y: "84%", r: 10, s: 56 },
          { id: "challenge", x: "86%", y: "82%", r: -10, s: 60 },
        ].map((it) => (
          <Box key={it.id} sx={{ position: "absolute", left: it.x, top: it.y, transform: `rotate(${it.r}deg)` }}>
            <PartyMascot
              shellId={it.id}
              size={it.s}
              animate={false}
              colors={{ hot: partyPal.hot, gold: partyPal.gold, violet: partyPal.violet, mint }}
            />
          </Box>
        ))}
      </Box>

      <Stack
        sx={{
          position: "relative",
          height: "100%",
          px: 7,
          pt: 7,
          pb: 6,
        }}
        spacing={5}
      >
        {/* header */}
        <Stack alignItems="center" spacing={1.25} sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src="/assets/kan.png"
            alt="Kan"
            crossOrigin="anonymous"
            sx={{
              height: 118,
              width: "auto",
              objectFit: "contain",
              opacity: 0.96,
              filter: "drop-shadow(0 10px 26px rgba(0,0,0,0.55))",
            }}
          />
          <Typography className="party-display" sx={{ fontSize: 72, lineHeight: 1, color: partyPal.ink, mt: 0.25 }}>
            {model.lang === "ar" ? "النتيجة" : "Result"}
          </Typography>
          <PartyMascot
            shellId={model.shellId}
            size={220}
            animate={false}
            colors={{
              hot: partyPal.hot,
              gold: partyPal.gold,
              violet: partyPal.violet,
              mint,
            }}
          />
          <Stack alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography className="party-display" sx={{ fontSize: 62, lineHeight: 1.02, textAlign: "center" }}>
              {model.modeTitle}
            </Typography>
            {model.resultTitle ? (
              <Typography className="party-mono" sx={{ fontSize: 16, letterSpacing: "0.18em", color: partyPal.inkDim }}>
                {model.resultTitle}
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        {/* main card */}
        <Box
          className="party-kan-card"
          sx={
            {
              ["--card-bg" as string]: "rgba(255,255,255,0.08)",
              border: `1.5px solid ${partyPal.inkFaint}`,
              backdropFilter: "blur(10px)",
              color: partyPal.ink,
              py: 7,
              px: 6,
              textAlign: "center",
              boxShadow: "0 28px 70px rgba(0,0,0,0.52)",
            } as React.CSSProperties
          }
        >
          <Typography
            className="party-display"
            sx={{
              fontSize: 112,
              lineHeight: 0.95,
              textShadow: `0 18px 38px rgba(0,0,0,0.55)`,
              filter: "drop-shadow(0 10px 26px rgba(0,0,0,0.45))",
            }}
          >
            {model.hero}
          </Typography>
          {model.sub ? (
            <Typography sx={{ mt: 2.5, fontSize: 32, fontWeight: 800, color: partyPal.inkDim, whiteSpace: "pre-wrap" }}>
              {model.sub}
            </Typography>
          ) : null}
        </Box>

        {/* scores / standings */}
        {model.scores && model.scores.length > 0 ? (
          <Stack direction="row" spacing={2.5}>
            {model.scores.slice(0, 3).map((s) => (
              <Box
                key={s.label}
                className="party-kan-card"
                sx={
                  {
                    ["--card-bg" as string]: "rgba(255,255,255,0.06)",
                    border: s.highlight
                      ? `2px solid ${s.color ?? partyPal.gold}`
                      : `1.5px solid ${partyPal.inkFaint}`,
                    color: partyPal.ink,
                    py: 3.75,
                    px: 3.5,
                    textAlign: "center",
                    flex: 1,
                    boxShadow: s.highlight ? `0 18px 42px color-mix(in oklab, ${s.color ?? partyPal.gold} 30%, transparent)` : "none",
                  } as React.CSSProperties
                }
              >
                <Typography className="party-mono" sx={{ fontSize: 16, letterSpacing: "0.16em", color: partyPal.inkDim }}>
                  {s.label}
                </Typography>
                <Typography
                  className="party-display"
                  sx={{
                    fontSize: 72,
                    mt: 1,
                    color: s.highlight ? (s.color ?? partyPal.gold) : partyPal.ink,
                  }}
                >
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : null}

        {model.standings && model.standings.length > 0 ? (
          <Box
            className="party-kan-card"
            sx={
              {
                ["--card-bg" as string]: "rgba(255,255,255,0.06)",
                border: `1.5px solid ${partyPal.inkFaint}`,
                color: partyPal.ink,
                py: 3.75,
                px: 3.5,
              } as React.CSSProperties
            }
          >
            <Stack spacing={1.25}>
              {model.standings.slice(0, 4).map((r) => (
                <Stack key={r.name} direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 900,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: r.highlight ? partyPal.gold : partyPal.ink,
                    }}
                  >
                    {r.name}
                  </Typography>
                  <Typography className="party-mono" sx={{ fontSize: 20, color: partyPal.inkDim, fontWeight: 800 }}>
                    {r.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}

        {model.meta && model.meta.length > 0 ? (
          <Stack spacing={1.25} sx={{ mt: "auto" }}>
            {model.meta.slice(0, 3).map((m, i) => (
              <Typography key={i} sx={{ fontSize: 20, color: partyPal.ink, fontWeight: 800, textAlign: "center", opacity: 0.95, whiteSpace: "pre-wrap" }}>
                {m}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Box sx={{ mt: "auto" }} />
        )}

        {/* footer */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ pt: 1.25, mt: "auto" }}>
          <Typography className="party-mono" sx={{ fontSize: 20, letterSpacing: "0.06em", color: partyPal.inkDim }}>
            Instagram {FOOTER_HANDLE}
          </Typography>
          <Typography className="party-mono" sx={{ fontSize: 15, color: partyPal.inkDim, textAlign: "center", maxWidth: 640, px: 2, lineHeight: 1.4 }}>
            {t("party.share.finePrint")}
          </Typography>
          <Typography className="party-mono" sx={{ fontSize: 20, letterSpacing: "0.06em", color: partyPal.inkDim }}>
            TikTok {FOOTER_HANDLE}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
});

